import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import emptyedu from '../../assets/img/colorkit.svg';
import { Plus, X, Eye, Edit2, FileText, Image, Search, Trash2 } from 'lucide-react';
import { getFileURL } from '../../utils/fileURL';
import {
    uploadDoctorFile,
    replaceToolboxFile,
    deleteDoctorFile,
    getAllDoctorFile,
} from '../../redux/doctorSlice';
import Pagination from '../Pagination';

function DocFilesTab() {
    const { t } = useTranslation(['docfilestab']);
    const dispatch = useDispatch();
    const { files, loading, error, selectedDoctor, paginationfiles } = useSelector((state) => state.doctor);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState('upload'); // 'upload' or 'replace'
    const [selectedFile, setSelectedFile] = useState(null);
    const [fileError, setFileError] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [searchError, setSearchError] = useState('');
    const [isUploading, setIsUploading] = useState(false);
    const [selectedFileId, setSelectedFileId] = useState(null);
    const [viewFile, setViewFile] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(true);

    // New form fields
    const [filename, setFilename] = useState('');
    const [description, setDescription] = useState('');
    const [filenameError, setFilenameError] = useState('');
    const [descriptionError, setDescriptionError] = useState('');

    const doctorId = selectedDoctor?.doctorId;
    const totalItems = paginationfiles?.totalFiles || 0;
    const currentPage = paginationfiles?.page || 1;
    const itemsPerPage = paginationfiles?.limit || 5;
    const totalPages = paginationfiles?.totalPages || 1;

    // Validation: No consecutive special chars in filename
    const hasConsecutiveSpecialChars = (str) => {
        return /[^a-zA-Z0-9\s\-_.]{2,}/.test(str);
    };

    // Validate search input
    const validateSearchInput = (value) => {
        // Sanitize input - remove dangerous characters
        const sanitized = value.replace(/[<>]/g, '');

        // Check length
        if (sanitized.length > 50) {
            return t('doctorFilesTab.search_exceed_50');
        }

        // Check for only whitespace
        if (sanitized.trim().length === 0 && sanitized.length > 0) {
            return t('doctorFilesTab.search_only_whitespace');
        }

        return '';
    };

    const validateFilename = (name) => {
        if (!name || name.trim() === '') return t('docfilestab.error_filename_required');
        if (/^\s+$/.test(name)) return t('doctorFilesTab.filename_only_whitespace');
        if (name.trim().length < 2) return t('doctorFilesTab.filename_min_2');
        if (name.length > 20) return t('docfilestab.error_filename_max');
        if (hasConsecutiveSpecialChars(name)) return t('docfilestab.error_filename_special_chars');
        return '';
    };

    const validateDescription = (desc) => {
        if (desc.length > 50) return t('docfilestab.error_description_max');
        if (/^\s+$/.test(desc) && desc.length > 0) return t('doctorFilesTab.description_only_whitespace');
        return '';
    };

    const validateFile = (file) => {
        if (!file) return t('docfilestab.error_no_file');

        const maxSize = 5 * 1024 * 1024; // 5MB
        const validTypes = ['image/jpeg', 'image/png', 'application/pdf'];

        if (!validTypes.includes(file.type)) {
            return t('docfilestab.error_invalid_file');
        }

        if (file.size > maxSize) {
            return t('docfilestab.error_file_size');
        }

        return '';
    };

    const handleAddClick = () => {
        setModalMode('upload');
        setIsModalOpen(true);
        resetModalFields();
    };

    const handleReplaceClick = (fileId, currentFile) => {
        setModalMode('replace');
        setSelectedFileId(fileId);
        setFilename(currentFile.filename.split('.').slice(0, -1).join('.') || '');
        setDescription(currentFile.description || '');
        setIsModalOpen(true);
        setSelectedFile(null);
        setFileError('');
    };

    const resetModalFields = () => {
        setSelectedFile(null);
        setFileError('');
        setFilename('');
        setDescription('');
        setFilenameError('');
        setDescriptionError('');
        setSelectedFileId(null);
        setModalMode('upload');
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        resetModalFields();
    };

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        const error = validateFile(file);

        if (error) {
            setFileError(error);
            setSelectedFile(null);
        } else {
            setFileError('');
            setSelectedFile(file);
            // Auto-fill filename from file name if empty
            if (!filename) {
                const nameWithoutExt = file.name.split('.').slice(0, -1).join('.');
                setFilename(nameWithoutExt.slice(0, 20));
            }
        }
    };

    // Handle search input change
    const handleSearchChange = (e) => {
        const value = e.target.value;

        // Sanitize input - remove dangerous characters
        const sanitized = value.replace(/[<>]/g, '');
        const finalValue = sanitized.length > 50
            ? sanitized.substring(0, 50)
            : sanitized;

        // Always update searchQuery so the input stays in sync
        setSearchQuery(finalValue);

        // Validate input
        const error = validateSearchInput(finalValue);
        setSearchError(error);
    };

    const handleFilenameChange = (e) => {
        const value = e.target.value;

        // Sanitize input - remove dangerous characters
        const sanitized = value.replace(/[<>]/g, '');
        // Enforce max length
        const finalValue = sanitized.length > 20 ? sanitized.substring(0, 20) : sanitized;

        setFilename(finalValue);
        const error = validateFilename(finalValue);
        setFilenameError(error);
    };

    const handleDescriptionChange = (e) => {
        const value = e.target.value;

        // Sanitize input - remove dangerous characters
        const sanitized = value.replace(/[<>]/g, '');
        // Enforce max length
        const finalValue = sanitized.length > 50 ? sanitized.substring(0, 50) : sanitized;

        setDescription(finalValue);
        const error = validateDescription(finalValue);
        setDescriptionError(error);
    };

    const handleSubmit = async () => {
        // Validate all fields
        const fileErr = validateFile(selectedFile);
        const nameErr = validateFilename(filename);
        const descErr = validateDescription(description);

        if (fileErr || nameErr) {
            setFileError(fileErr);
            setFilenameError(nameErr);
            setDescriptionError(descErr);
            return;
        }

        const result = await Swal.fire({
            title: modalMode === 'replace'
                ? t('docfilestab.replace_confirm_title')
                : t('docfilestab.upload_confirm_title'),
            text: modalMode === 'replace'
                ? t('docfilestab.replace_confirm_text')
                : t('docfilestab.upload_confirm_text'),
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: modalMode === 'replace'
                ? t('docfilestab.replace_confirm_yes')
                : t('docfilestab.upload_confirm_yes'),
            cancelButtonText: t('docfilestab.cancel'),
            confirmButtonColor: "#4ABDBD",
            cancelButtonColor: "#d33",
        });

        if (!result.isConfirmed) return;

        setIsUploading(true);
        const formData = new FormData();

        // Append file with correct key
        formData.append(modalMode === 'replace' ? 'file' : 'files', selectedFile);
        formData.append('filename', filename.trim());
        formData.append('description', description.trim());

        try {
            if (modalMode === 'replace') {
                await dispatch(
                    replaceToolboxFile({
                        doctorId,
                        fileData: formData,
                        fileId: selectedFileId,
                    })
                ).unwrap();
            } else {
                await dispatch(
                    uploadDoctorFile({
                        doctorId,
                        fileData: formData,
                    })
                ).unwrap();
            }

            handleCloseModal();

            toast.success(modalMode === 'replace'
                    ? t('docfilestab.replace_success_text')
                    : t('docfilestab.upload_success_text'));
        } catch (error) {
            console.error('Error during file upload/replace:', error);
            toast.warn(error?.error || t('docfilestab.error_general'));
        } finally {
            setIsUploading(false);
        }
    };

    const handleViewFile = (file) => {
        setPreviewLoading(true);
        setViewFile(file);
    };

    const handleCloseViewModal = () => {
        setViewFile(null);
    };

    const handleDeleteFile = async (fileId, filename) => {
        const result = await Swal.fire({
            title: t('docfilestab.delete_confirm_title'),
            text: t('docfilestab.confirm_delete', { filename }),
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: '#4ABDBD',
            cancelButtonColor: "#99a1af",
            confirmButtonText: t('docfilestab.delete_confirm'),
            cancelButtonText: t('docfilestab.cancel'),
        });

        if (!result.isConfirmed) return;

        try {
            await dispatch(deleteDoctorFile({ doctorId, fileId })).unwrap();

            toast.success(t('docfilestab.delete_success_text', { filename }));
        } catch (error) {
            toast.warn(error?.error || t('docfilestab.error_general'));
        }
    };

    const handlePageChange = (page, newItemsPerPage) => {
        const limit = newItemsPerPage || itemsPerPage;
        dispatch(getAllDoctorFile({ doctorId, patientId: 'null', date: 'null', page, limit }));
    };

    const query = searchQuery?.toLowerCase() || '';
    const filteredFiles = searchQuery
        ? (files || []).filter((file) => file?.filename?.toLowerCase().includes(query))
        : (files || []);

    return (
        <div data-testid="doctor-files-tab-container" className="mt-4">
            {/* Header Row */}
            <div data-testid="doctor-files-header" className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
                <h2 data-testid="doctor-files-title" className="text-lg sm:text-xl md:text-2xl font-bold bg-linear-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent">
                    {t('docfilestab.heading')}
                </h2>
                <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                    <div className="relative w-full sm:w-56 md:w-64">
                        <Search data-testid="doctor-files-search-icon" className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        <input
                            data-testid="doctor-files-search-input"
                            type="text"
                            placeholder={t('docfilestab.search_placeholder')}
                            value={searchQuery}
                            onChange={handleSearchChange}
                            maxLength={50}
                            className={`pl-10 px-4 py-2 w-full rounded-xl border focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] text-sm transition-all duration-300 hover:border-[#4ABDBD] ${searchError
                                    ? 'border-red-300 bg-red-50'
                                    : 'border-gray-300'
                                }`}
                        />
                        {searchError && (
                            <p data-testid="doctor-files-search-error" className="mt-1 text-xs text-red-500">{searchError}</p>
                        )}
                    </div>
                    <button
                        data-testid="add-file-button"
                        onClick={handleAddClick}
                        disabled={!doctorId}
                        className={`group flex items-center gap-2 w-full sm:w-auto cursor-pointer px-4 py-2 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 font-semibold relative overflow-hidden text-sm justify-center ${!doctorId ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                        <Plus data-testid="add-file-icon" className="w-5 h-5" />
                        <span data-testid="add-file-text">{t('docfilestab.add_file')}</span>
                    </button>
                </div>
            </div>

            {loading && (
                <p data-testid="doctor-files-loading-text" className="text-center text-gray-600 text-sm">
                    {t('docfilestab.loading')}
                </p>
            )}

            {!loading && (!files || files.length === 0) && (
                <div data-testid="doctor-files-empty-state" className="flex flex-col items-center justify-center col-span-full min-h-[200px] px-4 sm:px-6 lg:px-8">
                    <img
                        data-testid="doctor-files-empty-image"
                        src={emptyedu}
                        alt={t('docfilestab.no_education_found')}
                        className="w-24 h-24 sm:w-32 sm:h-32 md:w-40 md:h-40 lg:w-56 lg:h-56 object-contain"
                    />
                    <p data-testid="doctor-files-empty-text" className="text-gray-500 text-center text-sm sm:text-base md:text-lg">
                        {t('docfilestab.no_files')}
                    </p>
                </div>
            )}

            {!loading && filteredFiles.length > 0 && (
                <>
                    <div data-testid="doctor-files-grid" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-3 sm:gap-4">
                        {filteredFiles.map((file) => {
                            const FileIcon = file.fileType === 'pdf' ? FileText : Image;
                            return (
                                <div
                                    key={file._id}
                                    data-testid={`doctor-file-card-${file.fileId}`}
                                    className="group relative flex items-center gap-4 rounded-xl border border-gray-200 bg-gradient-to-r from-gray-50 to-blue-50/30 shadow-md hover:shadow-lg hover:shadow-[#4ABDBD]/20 transition-all duration-300 p-4 hover:scale-105"
                                >
                                    <div className="shrink-0 bg-linear-to-r from-[#4ABDBD]/20 to-[#3AA8A8]/20 p-3 rounded-xl shadow-sm">
                                        <FileIcon className="w-8 h-8 sm:w-10 sm:h-10 text-[#4ABDBD]" />
                                    </div>

                                    <div className="flex-1 min-w-0">
                                        <h3 data-testid={`doctor-file-name-${file.fileId}`} className="text-sm font-bold text-gray-700 group-hover:text-[#4ABDBD] transition-colors duration-300">
                                            {file.filename.length > 13
                                                ? `${file.filename.slice(0, 13)}...`
                                                : file.filename}
                                        </h3>
                                        <p data-testid={`doctor-file-date-${file.fileId}`} className="text-xs text-gray-600 mt-1">
                                            {t('docfilestab.added_at')} {new Date(file.createdAt).toLocaleDateString()}
                                        </p>
                                        {file.description && (
                                            <p data-testid={`doctor-file-description-${file.fileId}`} className="text-xs text-gray-500 mt-1 truncate">
                                                {file.description}
                                            </p>
                                        )}
                                    </div>

                                    <div className="flex gap-2 ml-auto opacity-70 group-hover:opacity-100 transition-all duration-300">
                                        <button
                                            data-testid={`view-file-${file.fileId}`}
                                            onClick={() => handleViewFile(file)}
                                            className="group/btn text-[#4ABDBD] hover:text-white p-2 rounded-xl bg-gradient-to-r from-blue-50 to-cyan-50 hover:from-[#4ABDBD] hover:to-[#3AA8A8] shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110"
                                            title={t('docfilestab.view_file_title')}
                                        >
                                            <Eye className="w-4 h-4" />
                                        </button>
                                        <button
                                            data-testid={`replace-file-${file.fileId}`}
                                            onClick={() => handleReplaceClick(file.fileId, file)}
                                            className="group/btn text-[#4ABDBD] hover:text-white p-2 rounded-xl bg-gradient-to-r from-blue-50 to-cyan-50 hover:from-[#4ABDBD] hover:to-[#3AA8A8] shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110"
                                            title={t('docfilestab.replace_file_title')}
                                        >
                                            <Edit2 className="w-4 h-4" />
                                        </button>
                                        <button
                                            data-testid={`delete-file-${file.fileId}`}
                                            onClick={() => handleDeleteFile(file.fileId, file.filename)}
                                            className="group/btn text-red-500 hover:text-white p-2 rounded-xl bg-gradient-to-r from-red-50 to-pink-50 hover:from-red-500 hover:to-pink-500 shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110"
                                            title={t('docfilestab.delete_file_title')}
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Pagination (server-side) */}
                    {totalItems > 0 && (
                        <div className="mt-4">
                            <Pagination
                                totalItems={totalItems}
                                currentPage={currentPage}
                                itemsPerPage={itemsPerPage}
                                onPageChange={handlePageChange}
                            />
                        </div>
                    )}
                </>
            )}

            {/* Upload / Replace Modal */}
            {isModalOpen && (
                <div
                    data-testid="doctor-file-modal"
                    className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
                    role="dialog"
                    aria-labelledby="file-modal-title"
                >
                    <div className="bg-white my-5 rounded-2xl shadow-2xl w-full max-w-md sm:max-w-lg md:max-w-2xl max-h-[80vh] sm:max-h-[90vh] flex flex-col">
                        <div className="p-4 sm:p-6 sticky top-0 bg-linear-to-r from-[#4ABDBD]/5 to-[#3AA8A8]/5 z-10 flex justify-between items-center border-b border-gray-200 rounded-t-2xl">
                            <h2
                                data-testid="doctor-file-modal-title"
                                className="text-base sm:text-lg md:text-xl font-bold bg-linear-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent"
                                id="file-modal-title"
                            >
                                {modalMode === 'replace' ? t('docfilestab.replace_file_heading') : t('docfilestab.upload_file_heading')}
                            </h2>
                            <button
                                data-testid="doctor-file-modal-close"
                                onClick={handleCloseModal}
                                aria-label={t('docfilestab.close_modal')}
                                className="group/btn text-gray-500 hover:text-white p-2 rounded-xl bg-gradient-to-r from-gray-100 to-gray-200 hover:from-gray-400 hover:to-gray-500 shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110"
                            >
                                <X className="w-5 h-5 sm:w-6 sm:h-6" />
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 custom-scrollbar space-y-4">
                            {/* File Input */}
                            <div>
                                <label data-testid="doctor-file-label" className="block text-sm font-semibold text-gray-700 mb-2">
                                    {t('docfilestab.select_file_label')}
                                </label>
                                <div className="relative">
                                    <input
                                        data-testid="doctor-file-input"
                                        type="file"
                                        id="file-input"
                                        accept="image/jpeg,image/png,application/pdf"
                                        onChange={handleFileChange}
                                        className="hidden"
                                    />
                                    <label
                                        htmlFor="file-input"
                                        className="flex items-center gap-4 w-full p-3 rounded-xl border border-gray-300 hover:border-[#4ABDBD] focus-within:ring-2 focus-within:ring-[#4ABDBD] transition-all duration-300 cursor-pointer"
                                    >
                                        <span data-testid="doctor-choose-file-text" className="px-4 py-2 rounded-xl bg-linear-to-r from-[#4ABDBD] to-[#3AA8A8] text-white text-sm font-semibold">
                                            {t('docfilestab.choose_file')}
                                        </span>
                                        <span data-testid="doctor-selected-file-name" className="text-sm text-gray-500 truncate">
                                            {selectedFile ? selectedFile.name : t('docfilestab.no_file_chosen')}
                                        </span>
                                    </label>
                                </div>
                                {fileError && (
                                    <p data-testid="doctor-file-error" className="mt-2 text-xs text-red-500 font-medium">{fileError}</p>
                                )}
                            </div>

                            {/* Filename Input */}
                            <div>
                                <label data-testid="doctor-filename-label" className="block text-sm font-semibold text-gray-700 mb-2">
                                    <span className="text-red-500">*</span> {t('docfilestab.filename_label')}
                                    <span className="text-xs text-gray-500 ml-1">({filename.length}/20)</span>
                                </label>
                                <input
                                    data-testid="doctor-filename-input"
                                    type="text"
                                    value={filename}
                                    onChange={handleFilenameChange}
                                    maxLength={20}
                                    placeholder={t('docfilestab.filename_placeholder')}
                                    className={`w-full px-4 py-2 rounded-xl border focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] text-sm transition-all duration-300 ${filenameError
                                            ? 'border-red-500 bg-red-50'
                                            : 'border-gray-300'
                                        }`}
                                />
                                {filenameError && (
                                    <p data-testid="doctor-filename-error" className="mt-1 text-xs text-red-500 font-medium">{filenameError}</p>
                                )}
                            </div>

                            {/* Description Input */}
                            <div>
                                <label data-testid="doctor-file-description-label" className="block text-sm font-semibold text-gray-700 mb-2">
                                    {t('docfilestab.description_label')}
                                    <span className={`text-xs ml-1 ${description.length > 50 ? 'text-red-500' : 'text-gray-500'}`}>
                                        ({description.length}/50)
                                    </span>
                                </label>
                                <textarea
                                    data-testid="doctor-file-description-input"
                                    value={description}
                                    onChange={handleDescriptionChange}
                                    maxLength={50}
                                    rows={3}
                                    placeholder={t('docfilestab.description_placeholder')}
                                    className={`w-full px-4 py-2 rounded-xl border focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] text-sm transition-all duration-300 resize-none ${descriptionError
                                            ? 'border-red-500 bg-red-50'
                                            : 'border-gray-300'
                                        }`}
                                />
                                {descriptionError && (
                                    <p data-testid="doctor-file-description-error" className="mt-1 text-xs text-red-500 font-medium">{descriptionError}</p>
                                )}
                            </div>
                        </div>

                        <div className="sticky bottom-0 p-4 sm:p-6 border-t rounded-2xl border-gray-200 bg-linear-to-r from-gray-50/50 to-blue-50/30 flex justify-end gap-4">
                            <button
                                data-testid="doctor-file-cancel-button"
                                type="button"
                                onClick={handleCloseModal}
                                className="px-4 py-2 bg-gradient-to-r from-gray-200 to-gray-300 hover:from-gray-300 hover:to-gray-400 text-gray-700 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 font-semibold text-sm hover:scale-105 cursor-pointer"
                            >
                                {t('docfilestab.cancel')}
                            </button>
                            <button
                                data-testid="doctor-file-submit-button"
                                type="button"
                                onClick={handleSubmit}
                                disabled={!selectedFile || isUploading || !!filenameError || !filename.trim()}
                                className={`group flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 font-semibold text-sm ${selectedFile && !isUploading && !filenameError && filename.trim()
                                        ? 'hover:scale-105 cursor-pointer'
                                        : 'opacity-50 cursor-not-allowed'
                                    }`}
                            >
                                {isUploading && (
                                    <span data-testid="doctor-file-loading-spinner" className="border-2 border-white border-t-transparent rounded-full w-4 h-4 animate-spin"></span>
                                )}
                                {isUploading
                                    ? t('docfilestab.uploading')
                                    : modalMode === 'replace'
                                        ? t('docfilestab.replace')
                                        : t('docfilestab.upload')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* View Modal */}
            {viewFile && (
                <div data-testid="doctor-file-view-modal" className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white my-5 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[80vh] sm:max-h-[90vh] flex flex-col">
                        <div className="p-4 sm:p-6 sticky top-0 bg-linear-to-r from-[#4ABDBD]/5 to-[#3AA8A8]/5 z-10 flex justify-between items-center border-b border-gray-200 rounded-t-2xl">
                            <h2
                                data-testid="doctor-file-view-modal-title"
                                className="text-base sm:text-lg md:text-xl font-bold bg-linear-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent truncate max-w-[70%]"
                                title={viewFile.filename}
                            >
                                {viewFile.filename.length > 30
                                    ? `${viewFile.filename.slice(0, 30)}...`
                                    : viewFile.filename}
                            </h2>
                            <button
                                data-testid="doctor-file-view-modal-close"
                                onClick={handleCloseViewModal}
                                className="group/btn text-gray-500 hover:text-white p-2 rounded-xl bg-gradient-to-r from-gray-100 to-gray-200 hover:from-gray-400 hover:to-gray-500 shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110"
                            >
                                <X className="w-5 h-5 sm:w-6 sm:h-6" />
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 custom-scrollbar">
                            {viewFile.fileType === 'pdf' ? (
                                <div className="relative w-full pb-[75%] sm:pb-[60%] md:pb-[56.25%] lg:pb-[50%] xl:pb-[45%]">
                                    <iframe
                                        src={getFileURL(viewFile.filePath)}
                                        className="absolute top-0 left-0 w-full h-full border-0 rounded-lg shadow"
                                        title={viewFile.filename}
                                        onLoad={() => setPreviewLoading(false)}
                                        onError={() => setPreviewLoading(false)}
                                    />
                                    {previewLoading && (
                                        <div className="absolute inset-0 flex items-center justify-center bg-white/80 rounded-lg">
                                            <span className="border-4 border-[#4ABDBD] border-t-transparent rounded-full w-10 h-10 animate-spin"></span>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="relative">
                                    <img
                                        src={getFileURL(viewFile.filePath)}
                                        alt={viewFile.filename}
                                        className="w-full h-auto max-h-full object-contain"
                                        onLoad={() => setPreviewLoading(false)}
                                        onError={() => setPreviewLoading(false)}
                                    />
                                    {previewLoading && (
                                        <div className="absolute inset-0 flex items-center justify-center bg-white/80 rounded-lg">
                                            <span className="border-4 border-[#4ABDBD] border-t-transparent rounded-full w-10 h-10 animate-spin"></span>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className="sticky bottom-0 p-4 sm:p-6 border-t rounded-2xl border-gray-200 bg-linear-to-r from-gray-50/50 to-blue-50/30 flex justify-end gap-4">
                            <a
                                data-testid="doctor-file-download-link"
                                href={getFileURL(viewFile.filePath)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="group flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 font-semibold text-sm hover:scale-105"
                            >
                                <span>{t('docfilestab.download')}</span>
                            </a>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default DocFilesTab;